import { useCallback, useEffect, useRef, useState } from 'react';

import { fetch_needs_attention_readiness } from '../api';
import { append_readiness_page, readiness_list_view } from './adapters';
import { readiness_params, teacher_search_error } from './params';

/*
 * the readiness list behind the close and reopen dialogs: teacher search sent as `q` (debounced),
 * "Load more" paging, and only the newest request may render.
 */
export const READINESS_PAGE_SIZE = 100;
export const SEARCH_DEBOUNCE_MS = 300;

const useReadinessList = ({ show, pay_month, state, query, on_error, error_text }) => {
  const [list, setList] = useState(readiness_list_view(null));
  const [listed_query, setListedQuery] = useState('');
  // 'list' while a first page loads (rows hidden), 'more' while a further page loads (rows kept).
  const [loading, setLoading] = useState('list');
  const [failed, setFailed] = useState('');
  const [settled_query, setSettledQuery] = useState(query);
  const request_seq = useRef(0);
  const list_ref = useRef(list);
  list_ref.current = list;
  // A close run calls reload from a closure made at submit time; it must still fetch what is on
  // screen when it ends, so every request reads its inputs here.
  const inputs_ref = useRef(null);
  inputs_ref.current = { pay_month, state, q: settled_query, on_error, error_text };

  // A keystroke is not a request; the text is sent once typing pauses.
  useEffect(() => {
    const t = setTimeout(() => setSettledQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const fetch_page = useCallback((append) => {
    const inputs = inputs_ref.current;
    request_seq.current += 1;
    const seq = request_seq.current;
    // an over-long search is never sent; the field says why, and no earlier rows stand in for it.
    if (teacher_search_error(inputs.q)) {
      setList(readiness_list_view(null));
      setListedQuery('');
      setFailed('search');
      setLoading('');
      return;
    }
    const offset = append ? list_ref.current.next_offset : 0;
    const params = readiness_params(
      { pay_month: inputs.pay_month, state: inputs.state, q: inputs.q },
      READINESS_PAGE_SIZE,
      offset
    );
    setLoading(append ? 'more' : 'list');
    setFailed('');
    fetch_needs_attention_readiness(params)
      .then((data) => {
        if (seq !== request_seq.current) return;
        const view = readiness_list_view(data);
        setList((prev) => (append ? append_readiness_page(prev, view) : view));
        setListedQuery(params.q);
        setLoading('');
      })
      .catch((e) => {
        if (seq !== request_seq.current) return;
        inputs.on_error(e, inputs.error_text);
        // A failed "Load more" keeps the rows already shown; a failed first page shows none.
        if (!append) setList(readiness_list_view(null));
        setFailed(append ? 'more' : 'list');
        setLoading('');
      });
  }, []);

  const reload = useCallback(() => fetch_page(false), [fetch_page]);
  const load_more = useCallback(() => fetch_page(true), [fetch_page]);

  useEffect(() => {
    if (show) reload();
  }, [show, pay_month, state, settled_query, reload]);

  return {
    list,
    listed_query,
    search_error: teacher_search_error(query),
    loading: loading === 'list',
    loading_more: loading === 'more',
    failed,
    reload,
    load_more,
    retry: failed === 'more' ? load_more : reload
  };
};

export default useReadinessList;
